import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import { 
    Building2, 
    Users, 
    UserPlus,
    ArrowLeft,
    Loader2,
    ExternalLink,
    LogOut,
    KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuperadminOrgDetailPage = () => {
    const { orgId } = useParams();
    const { token, logout } = useAuth();
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
    const [selectedUserForReset, setSelectedUserForReset] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    const [adminForm, setAdminForm] = useState({
        name: '',
        email: '',
        password: ''
    });
    
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        fetchOrganization();
    }, [orgId]);

    const fetchOrganization = async () => {
        try {
            const response = await axios.get(`${API}/superadmin/organizations/${orgId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch organization:', error);
            toast.error('Failed to load organization');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        
        if (!adminForm.name || !adminForm.email || !adminForm.password) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API}/superadmin/organizations/${orgId}/admin`,
                adminForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Admin user created successfully');
            setDialogOpen(false);
            setAdminForm({ name: '', email: '', password: '' });
            fetchOrganization();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/superadmin/login';
    };

    const openResetPasswordDialog = (user) => {
        setSelectedUserForReset(user);
        setNewPassword('');
        setResetPasswordDialogOpen(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (!newPassword || newPassword.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API}/superadmin/users/${selectedUserForReset.id}/reset-password`,
                { new_password: newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Passwort für ${selectedUserForReset.email} wurde zurückgesetzt`);
            setResetPasswordDialogOpen(false);
            setSelectedUserForReset(null);
            setNewPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-white mb-2">Organization not found</h2>
                    <Link to="/superadmin/organizations">
                        <Button className="bg-white text-slate-900">Go Back</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const { organization, users, stats } = data;

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/superadmin/organizations">
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                                <ArrowLeft size={20} />
                            </Button>
                        </Link>
                        <div className="flex items-center gap-3">
                            <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: organization.primary_color }}
                            >
                                {organization.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-white">{organization.name}</h1>
                                <p className="text-xs text-slate-400">/{organization.organization_code}</p>
                            </div>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        className="text-slate-300 hover:text-white hover:bg-slate-700"
                        onClick={handleLogout}
                    >
                        <LogOut size={18} className="mr-2" />
                        Sign Out
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6 animate-fade-in">
                    {/* Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                        <Users size={24} className="text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Total Users</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.total_users || 0}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                                        <UserPlus size={24} className="text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-400">Total Referrals</p>
                                        <p className="text-2xl font-bold text-white">
                                            {stats?.total_referrals || 0}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-6 flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-400 mb-1">Tenant URL</p>
                                    <p className="text-white font-medium">/o/{organization.organization_code}</p>
                                </div>
                                <a 
                                    href={`${window.location.origin}/o/${organization.organization_code}/login`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-slate-400 hover:text-white"
                                >
                                    <ExternalLink size={20} />
                                </a>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Users */}
                    <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-white">Users</CardTitle>
                            <Button
                                className="bg-white text-slate-900 hover:bg-slate-100"
                                onClick={() => setDialogOpen(true)}
                                data-testid="add-admin-btn"
                            >
                                <UserPlus size={18} className="mr-2" />
                                Add Admin
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {users.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users size={48} className="mx-auto text-slate-600 mb-3" />
                                    <p className="text-slate-400">No users yet</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-slate-700">
                                                <TableHead className="text-slate-400">Name</TableHead>
                                                <TableHead className="text-slate-400">Email</TableHead>
                                                <TableHead className="text-slate-400">Role</TableHead>
                                                <TableHead className="text-slate-400">Points</TableHead>
                                                <TableHead className="text-slate-400">Aktionen</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {users.map((user) => (
                                                <TableRow key={user.id} className="border-slate-700">
                                                    <TableCell className="text-white">{user.name}</TableCell>
                                                    <TableCell className="text-slate-300">{user.email}</TableCell>
                                                    <TableCell>
                                                        <Badge 
                                                            variant={user.role === 'org_admin' ? 'default' : user.role === 'sales_rep' ? 'outline' : 'secondary'}
                                                            className={
                                                                user.role === 'org_admin' 
                                                                    ? 'bg-blue-500/20 text-blue-400' 
                                                                    : user.role === 'sales_rep'
                                                                    ? 'border-purple-500/50 text-purple-400'
                                                                    : 'bg-slate-700 text-slate-300'
                                                            }
                                                        >
                                                            {user.role === 'org_admin' ? 'Admin' : user.role === 'sales_rep' ? 'Vertriebler' : 'Customer'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-white">{user.points_balance}</TableCell>
                                                    <TableCell>
                                                        {(user.role === 'org_admin' || user.role === 'sales_rep') && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-slate-400 hover:text-white hover:bg-slate-700"
                                                                onClick={() => openResetPasswordDialog(user)}
                                                                data-testid={`reset-password-btn-${user.id}`}
                                                            >
                                                                <KeyRound size={16} className="mr-1" />
                                                                Passwort
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* Add Admin Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white">Add Admin User</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Create an organization admin for {organization.name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="admin-name" className="text-slate-200">Name *</Label>
                            <Input
                                id="admin-name"
                                value={adminForm.name}
                                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                                placeholder="Admin Name"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="admin-name-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="admin-email" className="text-slate-200">Email *</Label>
                            <Input
                                id="admin-email"
                                type="email"
                                value={adminForm.email}
                                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                                placeholder="admin@example.com"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="admin-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="admin-password" className="text-slate-200">Password *</Label>
                            <Input
                                id="admin-password"
                                type="password"
                                value={adminForm.password}
                                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                                placeholder="Create a password"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="admin-password-input"
                            />
                        </div>
                        
                        <DialogFooter className="pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setDialogOpen(false)}
                                className="border-slate-600 text-slate-300"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-white text-slate-900 hover:bg-slate-100"
                                disabled={submitting}
                                data-testid="save-admin-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Admin'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white">Passwort zurücksetzen</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            {selectedUserForReset && (
                                <>Neues Passwort für <span className="text-white font-medium">{selectedUserForReset.email}</span> festlegen.
                                Der Benutzer muss beim nächsten Login ein neues Passwort setzen.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password" className="text-slate-200">Neues Passwort *</Label>
                            <Input
                                id="new-password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mindestens 6 Zeichen"
                                className="bg-slate-700 border-slate-600 text-white"
                                data-testid="new-password-input"
                                minLength={6}
                            />
                        </div>
                        
                        <DialogFooter className="pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={() => setResetPasswordDialogOpen(false)}
                                className="border-slate-600 text-slate-300"
                            >
                                Abbrechen
                            </Button>
                            <Button
                                type="submit"
                                className="bg-red-600 text-white hover:bg-red-700"
                                disabled={submitting}
                                data-testid="confirm-reset-password-btn"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Zurücksetzen...
                                    </>
                                ) : (
                                    <>
                                        <KeyRound className="mr-2 h-4 w-4" />
                                        Passwort zurücksetzen
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
